import React from 'react';
import { Edit2, Trash2, Calendar, Flag } from 'lucide-react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

const TaskItem = ({ task, onEdit, onDelete, onToggleComplete }) => {
  const handleToggleComplete = () => {
    onToggleComplete(task.id, { completed: !task.completed });
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM dd, yyyy');
  };

  const getDueDateClass = (dueDate) => {
    if (!dueDate) return '';
    
    const date = new Date(dueDate);
    if (isPast(date) && !task.completed) return 'overdue';
    if (isToday(date)) return 'today';
    if (isTomorrow(date)) return 'tomorrow';
    return '';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#e53e3e';
      case 'medium':
        return '#ed8936';
      case 'low':
        return '#38a169';
      default:
        return 'var(--bamboo-sage)';
    }
  };

  return (
    <div className={`task-item ${task.completed ? 'completed' : ''}`}>
      <div className="task-checkbox">
        <input
          type="checkbox"
          checked={task.completed}
          onChange={handleToggleComplete}
          className="checkbox"
        />
      </div>
      
      <div className="task-content">
        <div className="task-header">
          <h4 className="task-title">{task.title}</h4>
          <div className="task-meta">
            {task.priority && (
              <div 
                className="priority-badge"
                style={{ backgroundColor: getPriorityColor(task.priority) }}
              >
                <Flag size={12} />
                {task.priority}
              </div>
            )}
            {task.due_date && (
              <div className={`due-date ${getDueDateClass(task.due_date)}`}>
                <Calendar size={12} />
                {formatDueDate(task.due_date)}
              </div>
            )}
          </div>
        </div>
        
        {task.description && (
          <p className="task-description">{task.description}</p>
        )}
        
        <div className="task-footer">
          <span className="task-created">
            Created {format(new Date(task.created_at), 'MMM dd, yyyy')}
          </span>
        </div>
      </div>
      
      <div className="task-actions">
        <button
          onClick={() => onEdit(task)}
          className="btn-icon edit-btn"
          title="Edit task"
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onDelete(task.id)}
          className="btn-icon delete-btn"
          title="Delete task"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const TaskList = ({ tasks, onEdit, onDelete, onToggleComplete }) => {
  if (tasks.length === 0) {
    return (
      <div className="task-list-empty">
        <div className="empty-state">
          <div className="bamboo-zen">
            <div className="zen-circle"></div>
            <div className="zen-bamboo">
              <div className="zen-stalk"></div>
              <div className="zen-leaves">
                <div className="zen-leaf"></div>
                <div className="zen-leaf"></div>
              </div>
            </div>
          </div>
          <h3>Your mind is clear</h3>
          <p>No tasks found. Create a new task to get started on your journey to productivity.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-list">
      <div className="task-list-header">
        <h3>Your Tasks</h3>
        <span className="task-count">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
      </div>
      
      <div className="task-items">
        {tasks.map(task => (
          <TaskItem
            key={task.id}
            task={task}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleComplete={onToggleComplete}
          />
        ))}
      </div>
    </div>
  );
};

export default TaskList;
